// components/HomeRows.js
//
// The two compact rows on Home: a past invitation, and a greeting card.
//
// Both are small enough that a file each would be filing rather than
// organising, and they share a row shape — 84px thumbnail, two or three lines
// beside it — so keeping them together keeps that shape in one place.
//
// Past invitations stay as rows rather than becoming full cards. A full-bleed
// card is an invitation to act and there is nothing left to act on, while a
// wall of large cards makes the few upcoming ones harder to find — which is
// the opposite of what the big card is for.

import React from 'react';
import { IoMailOpenOutline, IoCreateOutline, IoCalendar } from 'react-icons/io5';
import { formatEventWhen, formatEventLocation } from '../shared/formatEvent';
import { isOpenDatePoll } from '../shared/sortEvents';
import { resolveEventThumbnail } from '../shared/eventCover';
import { firstMediaUrl } from '../utils/mediaUrl';
import './HomeRows.scss';

const FALLBACK_COVER = '/images/events/1.jpg';

// ── Past invitation ────────────────────────────────────────────────────────

export function InviteRow({ event, onOpen }) {
  const when = formatEventWhen(event);
  const where = formatEventLocation(event);

  return (
    <button type="button" className="home-row home-row--past" onClick={onOpen}>
      <img className="home-row-thumb" src={resolveEventThumbnail(event) || FALLBACK_COVER} alt="" loading="lazy" />

      <span className="home-row-text">
        <span className="home-row-title">{event.title}</span>
        {when ? <span className="home-row-sub">{when}</span> : null}
        {where ? <span className="home-row-sub home-row-sub--muted">{where}</span> : null}

        {isOpenDatePoll(event) ? (
          <span className="home-row-badge home-row-badge--warning">
            <IoCalendar />
            Pick a date
          </span>
        ) : null}
      </span>
    </button>
  );
}

// ── Greeting card ──────────────────────────────────────────────────────────

function firstName(person) {
  return (person?.first_name || person?.username || 'someone').trim();
}

/** "Ritu", "Ritu and Aman", "Ritu, Aman and 3 others". */
export function namesLine(people) {
  const n = people?.length ?? 0;
  if (n === 0) return '';
  if (n === 1) return firstName(people[0]);
  if (n === 2) return `${firstName(people[0])} and ${firstName(people[1])}`;
  const rest = n - 2;
  return `${firstName(people[0])}, ${firstName(people[1])} and ${rest} other${rest === 1 ? '' : 's'}`;
}

// Through firstMediaUrl, which drops the app's bundled-asset tokens and the
// Metro dev-server URLs some rows still carry — neither loads in a browser,
// and the second is blocked outright as mixed content on an HTTPS page.
function cardThumbnail(card) {
  if (card.cover_image_type === 'gif') {
    return firstMediaUrl(card.cover_static_gif_cdn_url, card.cover_image_cdn_url);
  }
  if (card.cover_image_type === 'youtube') {
    return firstMediaUrl(card.youtube_thumbnail_cdn_url, card.cover_thumbnail_cdn_url);
  }
  return firstMediaUrl(
    card.cover_image_cdn_url,
    card.cover_image_direct_url,
    card.cover_thumbnail_cdn_url,
  );
}

/**
 * One greeting card.
 *
 * A card sent to five people is ONE row with five faces, not five rows of the
 * same card. The line under the title says which way it went; a public card
 * nobody has messaged has no known people and says only that you made it,
 * because inventing a recipient would be worse than saying nothing.
 */
export function CardRow({ card, onOpen }) {
  const received = card.direction === 'received';
  const people = card.people ?? [];

  const line = people.length
    ? `${received ? 'From' : 'For'} ${namesLine(people)}`
    : received
      ? 'Sent to you'
      : card.privacy === 'private'
        ? 'Private card'
        : 'Made by you';

  const thumb = cardThumbnail(card);

  return (
    <button type="button" className="home-row" onClick={onOpen}>
      {thumb ? (
        <img className="home-row-thumb" src={thumb} alt="" loading="lazy" />
      ) : (
        <span className="home-row-thumb home-row-thumb--empty" aria-hidden="true">
          <IoMailOpenOutline />
        </span>
      )}

      <span className="home-row-text">
        <span className="home-row-title">{card.title}</span>
        <span className="home-row-sub">{line}</span>

        <span className="home-row-meta">
          {/* Up to three faces, then a count. More than three at 24px is a
              smear rather than a group of people. */}
          {people.length > 0 && (
            <span className="home-row-faces">
              {people.slice(0, 3).map((person) =>
                person.photo ? (
                  <img key={person.id} src={person.photo} alt="" loading="lazy" />
                ) : (
                  <span key={person.id} className="home-row-face-initial">
                    {firstName(person).charAt(0).toUpperCase()}
                  </span>
                ),
              )}
              {people.length > 3 && (
                <span className="home-row-face-initial">+{people.length - 3}</span>
              )}
            </span>
          )}

          <span className={`home-row-badge ${received ? 'home-row-badge--success' : 'home-row-badge--primary'}`}>
            {received ? <IoMailOpenOutline /> : <IoCreateOutline />}
            {received ? 'Received' : 'Created'}
          </span>
        </span>
      </span>
    </button>
  );
}
